
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

/**
 * Fetch recent notification history
 * @param limit Maximum number of notifications to retrieve
 * @param filter Filter options (success, type)
 * @returns Promise with notifications
 */
export const getTelegramNotificationHistory = async (
  limit: number = 50,
  filter: { success?: boolean, type?: string } = {}
) => {
  try {
    let query = supabase
      .from('telegram_notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);
    
    // Apply filters if provided
    if (filter.success !== undefined) {
      query = query.eq('success', filter.success);
    }
    
    if (filter.type) {
      query = query.eq('notification_type', filter.type);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Error fetching notification history:', error);
      return [];
    }
    
    return data || [];
  } catch (error) {
    console.error('Failed to fetch telegram notification history:', error);
    return [];
  }
};

/**
 * Get notification stats
 * @returns Promise with stats object
 */
export const getTelegramNotificationStats = async () => {
  try {
    // Get total count
    const { count: totalCount, error: totalError } = await supabase
      .from('telegram_notifications')
      .select('*', { count: 'exact', head: true });
    
    // Get failed count
    const { count: failedCount, error: failedError } = await supabase
      .from('telegram_notifications')
      .select('*', { count: 'exact', head: true })
      .eq('success', false);
    
    if (totalError || failedError) {
      console.error('Error fetching notification stats:', totalError || failedError);
      return { total: 0, success: 0, failed: 0 };
    }
    
    return {
      total: totalCount || 0,
      success: (totalCount || 0) - (failedCount || 0),
      failed: failedCount || 0
    };
  } catch (error) {
    console.error('Failed to fetch telegram notification stats:', error);
    return { total: 0, success: 0, failed: 0 };
  }
};

/**
 * Resend a failed notification
 * @param notificationId The ID of the failed notification to resend
 * @returns Promise with result
 */
export const resendTelegramNotification = async (notificationId: string) => {
  try {
    // First get the notification details
    const { data: notification, error: fetchError } = await supabase
      .from('telegram_notifications')
      .select('*')
      .eq('id', notificationId)
      .single();
    
    if (fetchError || !notification) {
      console.error('Error fetching notification to resend:', fetchError);
      return { success: false };
    }
    
    // Call the function to resend
    const { data, error } = await supabase.functions.invoke('telegram-bot', {
      body: {
        action: 'resend-notification',
        notificationId: notificationId
      }
    });
    
    if (error) {
      toast({
        title: 'Failed to resend notification',
        description: error.message,
        variant: 'destructive'
      });
      return { success: false };
    }
    
    toast({
      title: 'Notification resent',
      description: 'The notification has been queued for resending.',
      variant: 'default'
    });
    
    return data;
  } catch (error: any) {
    console.error('Failed to resend telegram notification:', error);
    toast({
      title: 'Failed to resend notification',
      description: error.message || 'An unexpected error occurred',
      variant: 'destructive'
    });
    return { success: false };
  }
};
